/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { Link } from '@tanstack/react-router';
import { FiChevronRight, FiClock } from 'react-icons/fi';

interface ProjectOverviewTabProps {
  project: any;
  projectMilestones: any[];
  canSeeBudget: boolean;
  budgetUsagePercent: number;
  projectExpenses: any[];
  openTasks: any[];
  toggleTaskStatus: (taskId: string) => void;
  setActiveTab: (tab: any) => void;
}

export const ProjectOverviewTab: React.FC<ProjectOverviewTabProps> = ({
  project,
  projectMilestones,
  canSeeBudget,
  budgetUsagePercent,
  projectExpenses,
  openTasks,
  toggleTaskStatus,
  setActiveTab,
}) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Left Column (3/5 width) */}
      <div className="lg:col-span-3 space-y-6">
        {/* About Section */}
        <div className="bg-[#0F0F0F] border border-white/5 rounded-3xl p-6">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">
            About This Project
          </h3>
          <p className="text-zinc-400 text-xs sm:text-sm leading-relaxed">{project.description}</p>
          {project.linkedEvent && (
            <div className="mt-6 flex items-center gap-3">
              <span className="text-zinc-550 text-xs">Linked Event:</span>
              <Link
                to="/events/$eventId"
                params={{ eventId: project.linkedEventUuid || '' }}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-semibold hover:bg-blue-500/20 transition-all cursor-pointer"
              >
                <span>{project.linkedEvent}</span>
                <FiChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
        </div>

        {/* Milestones Section */}
        <div className="bg-[#0F0F0F] border border-white/5 rounded-3xl p-6">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-5">
            Upcoming Milestones
          </h3>
          <div className="space-y-3.5">
            {projectMilestones.map((milestone) => (
              <div
                key={milestone.id}
                className="flex items-center justify-between p-4 bg-zinc-950/40 border border-white/5 rounded-2xl"
              >
                <div className="flex items-center gap-3.5">
                  <div
                    className={`p-2.5 rounded-full ${
                      milestone.status === 'In Progress'
                        ? 'bg-blue-500/10 text-blue-400'
                        : 'bg-zinc-800 text-zinc-500'
                    }`}
                  >
                    <FiClock className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-white text-xs sm:text-sm font-bold">{milestone.title}</p>
                    <p className="text-zinc-550 text-[10px] mt-0.5">{milestone.dueDate}</p>
                  </div>
                </div>
                <span
                  className={`text-[10px] font-bold ${
                    milestone.status === 'In Progress' ? 'text-blue-400' : 'text-zinc-500'
                  }`}
                >
                  {milestone.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Column (2/5 width) */}
      <div className="lg:col-span-2 space-y-6">
        {/* Budget Quick Summary commented out as Budget Tracker is coming soon
        {canSeeBudget && (
          <div className="bg-[#0F0F0F] border border-white/5 rounded-3xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Budget</h3>
              <span className="text-zinc-500 text-xs">
                ${project.spentBudget.toLocaleString()} of $
                {project.allocatedBudget.toLocaleString()}
              </span>
            </div>

            {/* Utilization bar * /}
            <div className="flex items-center justify-between text-xs font-semibold mb-2 mt-4">
              <span className="text-zinc-400">Usage</span>
              <span className="text-emerald-400">{budgetUsagePercent}%</span>
            </div>
            <div className="w-full h-2 bg-zinc-850 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full"
                style={{
                  width: `${budgetUsagePercent}%`,
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="bg-zinc-950/40 p-4 border border-white/5 rounded-2xl">
                <p className="text-zinc-550 text-[10px] font-bold uppercase tracking-wider">
                  Spent
                </p>
                <p className="text-white font-bold text-base mt-1.5">
                  ${project.spentBudget.toLocaleString()}
                </p>
              </div>
              <div className="bg-zinc-950/40 p-4 border border-white/5 rounded-2xl">
                <p className="text-zinc-555 text-[10px] font-bold uppercase tracking-wider">
                  Remaining
                </p>
                <p className="text-emerald-400 font-bold text-base mt-1.5">
                  ${Math.max(0, project.allocatedBudget - project.spentBudget).toLocaleString()}
                </p>
              </div>
            </div>

            <div className="mt-6 pt-5 border-t border-white/5 space-y-3">
              <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider mb-2">
                Recent Expenses
              </p>
              {projectExpenses.slice(0, 3).map((exp) => (
                <div
                  key={exp.id}
                  className="flex items-center justify-between text-xs font-semibold py-1"
                >
                  <span className="text-zinc-300">{exp.title}</span>
                  <span className="text-white">${exp.amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        */}

        {/* Open Tasks Quick View */}
        <div className="bg-[#0F0F0F] border border-white/5 rounded-3xl p-6">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-5">Open Tasks</h3>
          <div className="space-y-4">
            {openTasks.slice(0, 4).map((task) => (
              <div key={task.id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleTaskStatus(task.id)}
                    className="w-4 h-4 rounded-full border border-zinc-600 hover:border-zinc-300 flex items-center justify-center cursor-pointer transition-colors"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-transparent group-hover:bg-zinc-600" />
                  </button>
                  <div>
                    <p className="text-white text-xs font-semibold">{task.title}</p>
                    <p className="text-zinc-550 text-[10px] mt-0.5 flex items-center gap-1.5">
                      <span
                        className={`w-5 h-5 rounded-full ${task.assignee.color} text-[8px] font-bold text-white flex items-center justify-center`}
                      >
                        {task.assignee.initials}
                      </span>
                      <span>
                        {task.assignee.name} •{' '}
                        {task.dueDate &&
                        task.dueDate !== 'No due date' &&
                        task.dueDate !== 'No Date'
                          ? `Due ${task.dueDate}`
                          : 'No due date'}
                      </span>
                      {task.alerts?.includes('Overdue') && (
                        <span className="text-rose-500 font-bold uppercase text-[8px]">
                          Overdue
                        </span>
                      )}
                      {task.alerts?.includes('Due Soon') && (
                        <span className="text-amber-500 font-bold uppercase text-[8px]">
                          Due Soon
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <span
                  className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-md ${
                    task.priority === 'High'
                      ? 'bg-rose-500/10 text-rose-400'
                      : 'bg-amber-500/10 text-amber-400'
                  }`}
                >
                  {task.priority}
                </span>
              </div>
            ))}

            {openTasks.length > 4 && (
              <button
                onClick={() => setActiveTab('Tasks')}
                className="w-full text-center text-blue-400 text-xs font-semibold pt-2 hover:text-blue-300 transition-colors cursor-pointer"
              >
                +{openTasks.length - 4} more
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
